/*
|--------------------------------------------------------------------------
| Push cart upsell carousel
|--------------------------------------------------------------------------
| Order: a few pick-up items (rotating, mixed brands) -> Shopify
| recommendations for the newest cart items -> hand-picked products.
| Also drives the carousel arrows.
|
*/
if (!customElements.get('push-cart-upsell')) {
  const cache = new Map()
  // Always ask for the maximum so there are enough left after filtering (in cart / on sale)
  const FETCH_LIMIT = 10
  // One random order per page view, so the pick-ups don't reshuffle every time the bag updates
  const pickupRank = new Map()

  const rankFor = id => {
    if (!pickupRank.has(id)) pickupRank.set(id, Math.random())
    return pickupRank.get(id)
  }

  customElements.define(
    'push-cart-upsell',
    class PushCartUpsell extends HTMLElement {
      connectedCallback () {
        this.track = this.querySelector('[data-upsell-track]')
        this.prev = this.querySelector('[data-upsell-prev]')
        this.next = this.querySelector('[data-upsell-next]')
        if (!this.track) return

        this.updateArrows = this.updateArrows.bind(this)
        this.onPrev = () => this.step(-1)
        this.onNext = () => this.step(1)

        this.track.addEventListener('scroll', this.updateArrows, { passive: true })
        window.addEventListener('resize', this.updateArrows)
        this.prev?.addEventListener('click', this.onPrev)
        this.next?.addEventListener('click', this.onNext)

        const cards = [...this.track.querySelectorAll('.psh-UpsellCard')]
        this.pickups = this.choosePickups(cards.filter(card => card.hasAttribute('data-pickup')))
        this.fallbacks = cards.filter(card => !card.hasAttribute('data-pickup'))

        this.connected = true
        this.render([])
        if (!this.hasAttribute('data-static')) this.loadRecommendations()
      }

      disconnectedCallback () {
        this.connected = false
        this.track?.removeEventListener('scroll', this.updateArrows)
        window.removeEventListener('resize', this.updateArrows)
      }

      step (direction) {
        const card = this.track.querySelector('.psh-UpsellCard')
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0
        const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth

        this.track.scrollBy({ left: direction * distance, behavior: 'smooth' })
      }

      updateArrows () {
        if (!this.track) return
        const max = this.track.scrollWidth - this.track.clientWidth - 2

        this.toggleAttribute('data-scrollable', max > 0)
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2
        if (this.next) this.next.disabled = this.track.scrollLeft >= max
      }

      list (value) {
        return (value || '').split(',').map(v => v.trim()).filter(Boolean)
      }

      // Random pick from the pool, one per brand first so it's not three totes in a row
      choosePickups (pool) {
        const count = parseInt(this.dataset.pickupCount, 10) || 0
        if (!count || !pool.length) return []

        const shuffled = [...pool].sort((a, b) => rankFor(a.dataset.productId) - rankFor(b.dataset.productId))
        const chosen = []
        const vendors = new Set()

        shuffled.forEach(card => {
          if (chosen.length >= count || vendors.has(card.dataset.vendor)) return
          vendors.add(card.dataset.vendor)
          chosen.push(card)
        })
        shuffled.forEach(card => {
          if (chosen.length < count && !chosen.includes(card)) chosen.push(card)
        })

        return chosen
      }

      render (recommendationLists) {
        const limit = parseInt(this.dataset.limit, 10) || 8
        const seen = new Set(this.list(this.dataset.exclude))
        const cards = []
        const add = card => {
          const id = card.dataset.productId
          if (!id || seen.has(id)) return
          seen.add(id)
          cards.push(card)
        }

        this.pickups.forEach(add)
        const longest = Math.max(0, ...recommendationLists.map(l => l.length))
        for (let i = 0; i < longest; i++) recommendationLists.forEach(l => l[i] && add(l[i]))
        this.fallbacks.forEach(add)

        // Pick-ups always stay; the limit trims what comes after them
        const visible = cards.slice(0, Math.max(limit, this.pickups.length))
        this.track.replaceChildren(...visible.map(card => document.adoptNode(card)))
        this.track.scrollLeft = 0
        this.hidden = visible.length === 0
        this.updateArrows()
      }

      async fetchCards (productId, intent) {
        const key = `${productId}|${intent}`

        if (!cache.has(key)) {
          const url = new URL(this.dataset.url || '/recommendations/products', window.location.origin)
          url.searchParams.set('section_id', 'push-cart-upsell-recs')
          url.searchParams.set('product_id', productId)
          url.searchParams.set('limit', FETCH_LIMIT)
          url.searchParams.set('intent', intent)

          cache.set(
            key,
            fetch(url)
              .then(response => (response.ok ? response.text() : ''))
              .catch(() => ''),
          )
        }

        const html = await cache.get(key)
        if (!html) return []

        const doc = new DOMParser().parseFromString(html, 'text/html')
        let cards = [...doc.querySelectorAll('.psh-UpsellCard')]
        if (this.hasAttribute('data-exclude-sale')) {
          cards = cards.filter(card => !card.hasAttribute('data-on-sale'))
        }
        return cards
      }

      async loadRecommendations () {
        const seeds = this.list(this.dataset.productIds).slice(0, 2)
        if (!seeds.length) return

        const intent = this.dataset.intent || 'related'

        try {
          let lists = await Promise.all(seeds.map(id => this.fetchCards(id, intent)))

          if (intent !== 'related' && lists.every(l => !l.length)) {
            lists = await Promise.all(seeds.map(id => this.fetchCards(id, 'related')))
          }

          if (!this.connected || lists.every(l => !l.length)) return
          this.render(lists)
        } catch (error) {
          console.warn('[push-cart-upsell]', error)
        }
      }
    },
  )
}
